// The benefit configs — a faithful port of every rule in
// api/domain/assessment/config/eligibility/<benefit>/<benefit>.go, in exact
// declaration order, cross-checked against source 2026-07. Rule keys match the
// Go `Key` strings so the dev-panel trace lines up with production.
//
// The 15 production configs are reproduced verbatim, bugs and all (§7.3) — the
// deviations are commented inline where they bite. Carer's Allowance and Blue
// Badge (§7.4) do not exist in production; they are prototype-only and marked.

import {
  And,
  EligibleIfAllMetricsTrue,
  EligibleIfAnyMetricTrue,
  EligibleIfMetricTrue,
  IneligibleIfAllMetricsFalse,
  IneligibleIfMetricFalse,
  IneligibleIfMetricTrue,
  Or,
} from '@/benefits-flow/engine/combinators';
import { M } from '@/benefits-flow/engine/metrics';
import type { BenefitConfig } from '@/benefits-flow/engine/types';

// String identifiers, matching the production FundingType strings (plus the two
// prototype additions). Used everywhere as the stable benefit key.
export const B = {
  attendanceAllowance: 'attendance-allowance',
  universalCredit: 'universal-credit',
  pensionCredit: 'pension-credit',
  pip: 'personal-independence-payment',
  adp: 'adult-disability-payment',
  councilTaxReduction: 'council-tax-reduction',
  nhsFundedNursingCare: 'nhs-funded-nursing-care',
  freePersonalCare: 'free-personal-care',
  freeNursingCare: 'free-nursing-care',
  disabledFacilitiesGrant: 'disabled-facilities-grant',
  localAuthoritySocialCare: 'local-authority-social-care',
  busPassEngland: 'concessionary-bus-pass-england',
  busPassWales: 'concessionary-bus-pass-wales',
  busPassScotland: 'concessionary-bus-pass-scotland',
  busPassNorthernIreland: 'concessionary-bus-pass-northern-ireland',
  // Prototype-only
  carersAllowance: 'carers-allowance',
  blueBadge: 'blue-badge',
} as const;

// Attendance Allowance — note the AA config never reads condition-duration
// (§7.3 bug 1). Terminal illness short-circuits to eligible.
const attendanceAllowance: BenefitConfig = {
  id: B.attendanceAllowance,
  rules: [
    {
      key: 'resident-scotland',
      relevantKeys: [M.scotlandResident],
      apply: IneligibleIfMetricTrue(M.scotlandResident),
    },
    {
      key: 'not-over-pension-age',
      relevantKeys: [M.overStatePensionAge],
      apply: IneligibleIfMetricFalse(M.overStatePensionAge),
    },
    {
      key: 'is-terminally-ill',
      relevantKeys: [M.terminallyIll],
      apply: EligibleIfMetricTrue(M.terminallyIll),
    },
    {
      key: 'care-recipient-does-not-need-help',
      relevantKeys: [M.needsHelpNight, M.needsHelpDay],
      apply: IneligibleIfAllMetricsFalse(M.needsHelpNight, M.needsHelpDay),
    },
    {
      key: 'care-recipient-needs-help',
      relevantKeys: [M.needsHelpDay, M.needsHelpNight],
      apply: EligibleIfAnyMetricTrue(M.needsHelpDay, M.needsHelpNight),
    },
  ],
};

// PIP — no lower age bound (§7.3 bug 5). And(...) never emits ineligible, so a
// non-resident with an under-pension-age fact falls through to more-info.
const pip: BenefitConfig = {
  id: B.pip,
  rules: [
    {
      key: 'not-under-state-pension-age',
      relevantKeys: [M.underStatePensionAge],
      apply: IneligibleIfMetricFalse(M.underStatePensionAge),
    },
    {
      key: 'condition-duration-and-resident-ewni',
      relevantKeys: [
        M.englandResident,
        M.walesResident,
        M.northernIrelandResident,
        M.conditionDuration6Months,
        M.conditionDuration12MonthsExpected,
        M.terminallyIll,
      ],
      apply: And(
        EligibleIfAnyMetricTrue(
          M.englandResident,
          M.walesResident,
          M.northernIrelandResident,
        ),
        EligibleIfAnyMetricTrue(
          M.conditionDuration6Months,
          M.conditionDuration12MonthsExpected,
          M.terminallyIll,
        ),
      ),
    },
  ],
};

// Adult Disability Payment (Scotland) — no age bound at all (§7.3 bug 5).
const adp: BenefitConfig = {
  id: B.adp,
  rules: [
    {
      key: 'not-resident-scotland',
      relevantKeys: [M.scotlandResident],
      apply: IneligibleIfMetricFalse(M.scotlandResident),
    },
    {
      key: 'condition-duration-and-resident-scotland',
      relevantKeys: [
        M.scotlandResident,
        M.conditionDuration6Months,
        M.conditionDuration12MonthsExpected,
        M.terminallyIll,
      ],
      apply: And(
        EligibleIfMetricTrue(M.scotlandResident),
        EligibleIfAnyMetricTrue(
          M.conditionDuration6Months,
          M.conditionDuration12MonthsExpected,
          M.terminallyIll,
        ),
      ),
    },
  ],
};

const pensionCredit: BenefitConfig = {
  id: B.pensionCredit,
  rules: [
    {
      key: 'not-over-state-pension-age',
      relevantKeys: [M.overStatePensionAge],
      apply: IneligibleIfMetricFalse(M.overStatePensionAge),
    },
    {
      key: 'single-over-state-pension-age-income-below-threshold',
      relevantKeys: [M.overStatePensionAge, M.isSingle, M.weeklyIncomeBelow238],
      apply: EligibleIfAllMetricsTrue(
        M.overStatePensionAge,
        M.isSingle,
        M.weeklyIncomeBelow238,
      ),
    },
    {
      key: 'couple-both-over-state-pension-age-income-below-threshold',
      relevantKeys: [
        M.overStatePensionAge,
        M.hasPartner,
        M.partnerOverStatePensionAge,
        M.jointWeeklyIncomeBelow364,
      ],
      apply: EligibleIfAllMetricsTrue(
        M.overStatePensionAge,
        M.hasPartner,
        M.partnerOverStatePensionAge,
        M.jointWeeklyIncomeBelow364,
      ),
    },
  ],
};

// Council Tax Reduction — the residency gate omits Northern Ireland (§7.3
// bug 2). An NI resident has england/wales/scotland present-and-false, so the
// IneligibleIfAllMetricsFalse gate rules them ineligible at rule 1.
const councilTaxReduction: BenefitConfig = {
  id: B.councilTaxReduction,
  rules: [
    {
      key: 'not-gb-resident',
      relevantKeys: [M.englandResident, M.walesResident, M.scotlandResident],
      apply: IneligibleIfAllMetricsFalse(
        M.englandResident,
        M.walesResident,
        M.scotlandResident,
      ),
    },
    {
      key: 'savings-above-16k',
      relevantKeys: [M.savingsBelow16k],
      apply: IneligibleIfMetricFalse(M.savingsBelow16k),
    },
    {
      key: 'single-savings-below-16k-income-below-300',
      relevantKeys: [M.savingsBelow16k, M.isSingle, M.weeklyIncomeBelow300],
      apply: EligibleIfAllMetricsTrue(
        M.savingsBelow16k,
        M.isSingle,
        M.weeklyIncomeBelow300,
      ),
    },
    {
      key: 'couple-savings-below-16k-joint-income-below-450',
      relevantKeys: [
        M.savingsBelow16k,
        M.hasPartner,
        M.jointWeeklyIncomeBelow450,
      ],
      apply: EligibleIfAllMetricsTrue(
        M.savingsBelow16k,
        M.hasPartner,
        M.jointWeeklyIncomeBelow450,
      ),
    },
  ],
};

const disabledFacilitiesGrant: BenefitConfig = {
  id: B.disabledFacilitiesGrant,
  rules: [
    {
      key: 'not-disabled',
      relevantKeys: [M.isDisabled],
      apply: IneligibleIfMetricFalse(M.isDisabled),
    },
    {
      key: 'home-does-not-require-adaptations',
      relevantKeys: [M.homeRequiresAdaptations],
      apply: IneligibleIfMetricFalse(M.homeRequiresAdaptations),
    },
    {
      key: 'disabled-and-home-requires-adaptations',
      relevantKeys: [M.isDisabled, M.homeRequiresAdaptations],
      apply: EligibleIfAllMetricsTrue(M.isDisabled, M.homeRequiresAdaptations),
    },
  ],
};

const universalCredit: BenefitConfig = {
  id: B.universalCredit,
  rules: [
    {
      key: 'not-uk-resident',
      relevantKeys: [M.ukResident],
      apply: IneligibleIfMetricFalse(M.ukResident),
    },
    {
      key: 'not-over-18',
      relevantKeys: [M.over18],
      apply: IneligibleIfMetricFalse(M.over18),
    },
    {
      key: 'not-under-state-pension-age',
      relevantKeys: [M.underStatePensionAge],
      apply: IneligibleIfMetricFalse(M.underStatePensionAge),
    },
    {
      key: 'over-16k-savings',
      relevantKeys: [M.savingsBelow16k],
      apply: IneligibleIfMetricFalse(M.savingsBelow16k),
    },
    {
      key: 'under-16k-savings',
      relevantKeys: [
        M.ukResident,
        M.over18,
        M.underStatePensionAge,
        M.savingsBelow16k,
      ],
      apply: EligibleIfAllMetricsTrue(
        M.ukResident,
        M.over18,
        M.underStatePensionAge,
        M.savingsBelow16k,
      ),
    },
  ],
};

// NHS-funded Nursing Care — RelevantMetricKeys lists needs-nursing-care but the
// rule never reads it (§7.3 bug 3). Only in-nursing-home counts.
const nhsFundedNursingCare: BenefitConfig = {
  id: B.nhsFundedNursingCare,
  rules: [
    {
      key: 'resident-scotland',
      relevantKeys: [M.scotlandResident],
      apply: IneligibleIfMetricTrue(M.scotlandResident),
    },
    {
      key: 'resident-ewni-in-nursing-home-or-needs-nursing-care',
      // needs-nursing-care is declared relevant in source but dead — kept here
      // to mirror the RelevantMetricKeys, though apply never reads it.
      relevantKeys: [
        M.englandResident,
        M.walesResident,
        M.northernIrelandResident,
        M.inNursingHome,
        M.needsNursingCare,
      ],
      apply: And(
        EligibleIfAnyMetricTrue(
          M.englandResident,
          M.walesResident,
          M.northernIrelandResident,
        ),
        EligibleIfMetricTrue(M.inNursingHome),
      ),
    },
  ],
};

const freePersonalCare: BenefitConfig = {
  id: B.freePersonalCare,
  rules: [
    {
      key: 'not-resident-scotland',
      relevantKeys: [M.scotlandResident],
      apply: IneligibleIfMetricFalse(M.scotlandResident),
    },
    {
      key: 'resident-scotland-needs-personal-care',
      relevantKeys: [M.scotlandResident, M.needsPersonalCare],
      apply: EligibleIfAllMetricsTrue(M.scotlandResident, M.needsPersonalCare),
    },
  ],
};

const freeNursingCare: BenefitConfig = {
  id: B.freeNursingCare,
  rules: [
    {
      key: 'not-resident-scotland',
      relevantKeys: [M.scotlandResident],
      apply: IneligibleIfMetricFalse(M.scotlandResident),
    },
    {
      key: 'resident-scotland-needs-nursing-care',
      relevantKeys: [M.scotlandResident, M.needsNursingCare],
      apply: EligibleIfAllMetricsTrue(M.scotlandResident, M.needsNursingCare),
    },
  ],
};

const localAuthoritySocialCare: BenefitConfig = {
  id: B.localAuthoritySocialCare,
  rules: [
    {
      key: 'savings-above-23250',
      relevantKeys: [M.savingsBelow23250],
      apply: IneligibleIfMetricFalse(M.savingsBelow23250),
    },
    {
      key: 'does-not-need-help-day',
      relevantKeys: [M.needsHelpDay],
      apply: IneligibleIfMetricFalse(M.needsHelpDay),
    },
    {
      key: 'savings-below-23250-and-needs-help-day',
      relevantKeys: [M.savingsBelow23250, M.needsHelpDay],
      apply: EligibleIfAllMetricsTrue(M.savingsBelow23250, M.needsHelpDay),
    },
  ],
};

// Bus passes. England and Wales carry the full disability route; Scotland and
// NI are age-60-only with a // TODO in source (§7.3 bug 4).
const busPassEngland: BenefitConfig = {
  id: B.busPassEngland,
  rules: [
    {
      key: 'not-england-wales-resident',
      relevantKeys: [M.englandResident],
      apply: IneligibleIfMetricFalse(M.englandResident),
    },
    {
      key: 'eligible-over-state-pension-age',
      relevantKeys: [M.englandResident, M.overStatePensionAge],
      apply: EligibleIfAllMetricsTrue(M.englandResident, M.overStatePensionAge),
    },
    {
      key: 'eligible-blind',
      relevantKeys: [M.englandResident, M.isBlindOrPartiallySighted],
      apply: EligibleIfAllMetricsTrue(
        M.englandResident,
        M.isBlindOrPartiallySighted,
      ),
    },
    {
      key: 'eligible-deaf',
      relevantKeys: [M.englandResident, M.isDeaf],
      apply: EligibleIfAllMetricsTrue(M.englandResident, M.isDeaf),
    },
    {
      key: 'eligible-without-speech',
      relevantKeys: [M.englandResident, M.withoutSpeech],
      apply: EligibleIfAllMetricsTrue(M.englandResident, M.withoutSpeech),
    },
    {
      key: 'eligible-learning-disability',
      relevantKeys: [M.englandResident, M.hasLearningDisability],
      apply: EligibleIfAllMetricsTrue(
        M.englandResident,
        M.hasLearningDisability,
      ),
    },
    {
      key: 'eligible-walking-disability',
      relevantKeys: [M.englandResident, M.hasWalkingDisability],
      apply: EligibleIfAllMetricsTrue(
        M.englandResident,
        M.hasWalkingDisability,
      ),
    },
    {
      key: 'eligible-no-arms-or-loss-of-use-of-arms',
      relevantKeys: [M.englandResident, M.hasNoArms],
      apply: EligibleIfAllMetricsTrue(M.englandResident, M.hasNoArms),
    },
  ],
};

// Wales — same shape as England but gated on over-60, not pension age.
const busPassWales: BenefitConfig = {
  id: B.busPassWales,
  rules: [
    {
      key: 'not-wales-resident',
      relevantKeys: [M.walesResident],
      apply: IneligibleIfMetricFalse(M.walesResident),
    },
    {
      key: 'eligible-over-60',
      relevantKeys: [M.walesResident, M.over60],
      apply: EligibleIfAllMetricsTrue(M.walesResident, M.over60),
    },
    {
      key: 'eligible-blind',
      relevantKeys: [M.walesResident, M.isBlindOrPartiallySighted],
      apply: EligibleIfAllMetricsTrue(
        M.walesResident,
        M.isBlindOrPartiallySighted,
      ),
    },
    {
      key: 'eligible-deaf',
      relevantKeys: [M.walesResident, M.isDeaf],
      apply: EligibleIfAllMetricsTrue(M.walesResident, M.isDeaf),
    },
    {
      key: 'eligible-without-speech',
      relevantKeys: [M.walesResident, M.withoutSpeech],
      apply: EligibleIfAllMetricsTrue(M.walesResident, M.withoutSpeech),
    },
    {
      key: 'eligible-learning-disability',
      relevantKeys: [M.walesResident, M.hasLearningDisability],
      apply: EligibleIfAllMetricsTrue(M.walesResident, M.hasLearningDisability),
    },
    {
      key: 'eligible-walking-disability',
      relevantKeys: [M.walesResident, M.hasWalkingDisability],
      apply: EligibleIfAllMetricsTrue(M.walesResident, M.hasWalkingDisability),
    },
    {
      key: 'eligible-no-arms-or-loss-of-use-of-arms',
      relevantKeys: [M.walesResident, M.hasNoArms],
      apply: EligibleIfAllMetricsTrue(M.walesResident, M.hasNoArms),
    },
  ],
};

// Scotland — age-60 only, single-metric positive rule. No disability route:
// source carries `// TODO[benefits-calculator]: add rules for disabilities`.
const busPassScotland: BenefitConfig = {
  id: B.busPassScotland,
  rules: [
    {
      key: 'not-scotland-resident',
      relevantKeys: [M.scotlandResident],
      apply: IneligibleIfMetricFalse(M.scotlandResident),
    },
    {
      key: 'eligible-over-60',
      relevantKeys: [M.over60],
      apply: EligibleIfMetricTrue(M.over60),
    },
  ],
};

// Northern Ireland — age-60 only, same missing disability route (TODO in source).
const busPassNorthernIreland: BenefitConfig = {
  id: B.busPassNorthernIreland,
  rules: [
    {
      key: 'not-northern-ireland-resident',
      relevantKeys: [M.northernIrelandResident],
      apply: IneligibleIfMetricFalse(M.northernIrelandResident),
    },
    {
      key: 'eligible-over-60',
      relevantKeys: [M.over60],
      apply: EligibleIfMetricTrue(M.over60),
    },
  ],
};

// ── Prototype-only benefits (§7.4). Not in the production FundingType enum. ──

// Carer's Allowance. Rules 1–5 gate ineligibility exactly as the brief lists;
// the "cared-for person holds no qualifying award" case is NOT modelled here as
// ineligible — it is a BLOCKED action state resolved downstream (engine/action.ts),
// because a blocked claim is genuinely eligible, just not yet claimable.
const carersAllowance: BenefitConfig = {
  id: B.carersAllowance,
  rules: [
    {
      key: 'no-carer',
      relevantKeys: [M.carerPresent],
      apply: IneligibleIfMetricFalse(M.carerPresent),
    },
    {
      key: 'carer-under-16',
      relevantKeys: [M.carerOver16],
      apply: IneligibleIfMetricFalse(M.carerOver16),
    },
    {
      key: 'carer-in-full-time-education',
      relevantKeys: [M.carerFullTimeEducation],
      apply: IneligibleIfMetricTrue(M.carerFullTimeEducation),
    },
    {
      key: 'carer-under-35-hours',
      relevantKeys: [M.carerHours35Plus],
      apply: IneligibleIfMetricFalse(M.carerHours35Plus),
    },
    {
      key: 'carer-earnings-at-or-above-204',
      relevantKeys: [M.carerEarningsBelow204],
      apply: IneligibleIfMetricFalse(M.carerEarningsBelow204),
    },
    {
      key: 'carer-meets-conditions',
      relevantKeys: [
        M.carerPresent,
        M.carerOver16,
        M.carerHours35Plus,
        M.carerEarningsBelow204,
      ],
      apply: EligibleIfAllMetricsTrue(
        M.carerPresent,
        M.carerOver16,
        M.carerHours35Plus,
        M.carerEarningsBelow204,
      ),
    },
  ],
};

// Blue Badge. Eligibility is gated on a disability being present; the
// automatic vs discretionary vs check-award route (which turns on PIP points /
// mobility awards we can't fully observe) is resolved downstream in action.ts.
const blueBadge: BenefitConfig = {
  id: B.blueBadge,
  rules: [
    {
      key: 'not-disabled',
      relevantKeys: [M.isDisabled],
      apply: IneligibleIfMetricFalse(M.isDisabled),
    },
    {
      key: 'disabled',
      relevantKeys: [M.isDisabled],
      apply: EligibleIfMetricTrue(M.isDisabled),
    },
  ],
};

// Registry. Order here is the order results are computed and displayed; the
// value-ranked variant re-sorts, the others group.
export const PRODUCTION_CONFIGS: BenefitConfig[] = [
  attendanceAllowance,
  pip,
  adp,
  pensionCredit,
  councilTaxReduction,
  disabledFacilitiesGrant,
  universalCredit,
  nhsFundedNursingCare,
  freePersonalCare,
  freeNursingCare,
  localAuthoritySocialCare,
  busPassEngland,
  busPassWales,
  busPassScotland,
  busPassNorthernIreland,
];

export const PROTOTYPE_CONFIGS: BenefitConfig[] = [carersAllowance, blueBadge];

export const ALL_CONFIGS: BenefitConfig[] = [
  ...PRODUCTION_CONFIGS,
  ...PROTOTYPE_CONFIGS,
];
